import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import Link from '../../../Link';
import Box from '../../../../shared/Box/index.tsx';
import { Caret } from '../../../../shared/Caret';
import IconBadge from '../../../IconBadge';
import { clickNavLink, clickedNavLinkGoogleAnalytics } from '../../events';

import styles from './styles.sass';
import LinkStyles from '../../LinkStyles.sass';

const cx = classnames.bind(LinkStyles);

const scrollElementIntoView = id => {
  setTimeout(() => {
    const el = document.getElementById(id);
    el.scrollIntoView({ behavior: 'smooth' });
  }, 100);
};

const NavLinks = ({ links, pathname }) => {
  const [showSubLinks, setShowSubLinks] = useState(false);

  /**
   * add/remove keydown listener to hide the sub nav menu by hitting escape key
   */
  useEffect(() => {
    function handleHideSubLinks(e) {
      if (e.key === 'Escape') setShowSubLinks(false);
    }
    if (showSubLinks) {
      document.addEventListener('keydown', handleHideSubLinks);
    } else {
      document.removeEventListener('keydown', handleHideSubLinks);
    }
    return () => document.removeEventListener('keydown', handleHideSubLinks);
  }, [showSubLinks]);

  /**
   * On unmount, always hide the sub nav menu
   */
  useEffect(() => () => setShowSubLinks(false), []);

  return (
    <ul className={styles.navLinksList}>
      {links.map(link => {
        const {
          label,
          key,
          to,
          href,
          subLinks,
          isShowingNotificationBadge,
          notificationBadge,
          openInNewTab,
        } = link;
        return (
          <li key={key} className={styles.linkItem}>
            {!subLinks ? (
              <Link
                to={to}
                href={href}
                onClick={() => {
                  clickNavLink({ link: key });
                  clickedNavLinkGoogleAnalytics(label);
                }}
                className={cx({ link: true, linkActive: pathname === to })}
                target={openInNewTab ? '_blank' : '_self'}
              >
                <span>
                  {label}
                  {isShowingNotificationBadge && (
                    <Box
                      position="absolute"
                      top
                      right
                      marginTop={0}
                      marginRight={-10}
                    >
                      <IconBadge
                        backgroundColor="green"
                        iconColor="#292F36"
                        width={10}
                        padding={1}
                        type={notificationBadge}
                      />
                    </Box>
                  )}
                </span>
              </Link>
            ) : (
              <button
                aria-label="Features menu toggle"
                aria-controls="features-menu"
                aria-expanded={showSubLinks}
                tabIndex={0}
                className={`withSubLinks ${cx({
                  link: true,
                  linkActive: pathname === to,
                })}`}
                onClick={() => {
                  setShowSubLinks(!showSubLinks);
                }}
              >
                {label} {subLinks && <Caret className={LinkStyles.caret} />}
              </button>
            )}
            {showSubLinks && subLinks && (
              <ul className={`subLinks ${styles.subLinks}`} id="features-menu">
                {subLinks.map((subLink, index) => {
                  const {
                    anchorId,
                    label: subLinkLabel,
                    description: subLinkDescription,
                    to: subLinkTo,
                    svg,
                    ariaLabel,
                  } = subLink;
                  return (
                    <li className={styles.subLinkItem} key={subLinkLabel}>
                      <Link
                        aria-label={ariaLabel}
                        to={subLinkTo}
                        className={`${styles.subLink}`}
                        onClick={() => {
                          setShowSubLinks(false);
                          clickedNavLinkGoogleAnalytics(subLinkLabel);
                          if (anchorId) scrollElementIntoView(anchorId);
                        }}
                        onKeyDown={e => {
                          // if you tab on the last sub link, close the menu
                          if (
                            index === subLinks.length - 1 &&
                            e.key === 'Tab' &&
                            !e.shiftKey
                          ) {
                            setShowSubLinks(false);
                          }
                        }}
                      >
                        <span className={styles.subLinkContainer}>
                          <span className={styles.subLinkSvgContainer}>
                            {svg}
                          </span>
                          <span>
                            {subLinkLabel}
                            <span className={styles.subLinkDescription}>
                              {subLinkDescription}
                            </span>
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

NavLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      key: PropTypes.string,
      to: PropTypes.string,
      subLinks: PropTypes.arrayOf(
        PropTypes.shape({
          to: PropTypes.string,
          label: PropTypes.string,
          svg: PropTypes.element,
        })
      ),
      isShowingNotificationBadge: PropTypes.bool,
      notificationBadge: PropTypes.string,
    })
  ).isRequired,
  pathname: PropTypes.string.isRequired,
};

export { NavLinks };
